import { useState, useEffect, useRef, useCallback } from 'react';
import { type Consumer, type Subscription } from '@rails/actioncable';

interface Data {
  channel: string
  id?: number
}

interface Callbacks<T> {
  received?: (_message: T) => void
  initialized?: () => void
  connected?: () => void
  disconnected?: () => void
}

export default function useChannel<ReceivedType>(actionCable: Consumer) {
  const [connected, setConnected] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const channelRef = useRef<Subscription<Consumer> | null>(null);

  const subscribe = useCallback((data: Data, callbacks: Callbacks<ReceivedType>) => {
    const channel = actionCable.subscriptions.create(data, {
      received: (message: ReceivedType) => {
        if (callbacks.received) {
          callbacks.received(message);
        }
      },
      initialized: () => {
        setSubscribed(true);
        if (callbacks.initialized) {
          callbacks.initialized();
        }
      },
      connected: () => {
        setConnected(true);
        if (callbacks.connected) {
          callbacks.connected();
        }
      },
      disconnected: () => {
        setConnected(false);
        if (callbacks.disconnected) {
          callbacks.disconnected();
        }
      }
    });
    channelRef.current = channel;
  }, [actionCable]);

  const unsubscribe = useCallback(() => {
    setSubscribed(false);
    if (channelRef.current) {
      channelRef.current.unsubscribe();
      channelRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, [unsubscribe]);

  const send = (action: string, payload: {} | undefined) => {
    if (subscribed && !connected) {
      throw new Error('useChannel - ERROR: not connected');
    }

    if (!subscribed) {
      throw new Error('useChannel - ERROR: not subscribed');
    }

    try {
      channelRef?.current?.perform(action, payload);
    } catch (e: any) {
      throw new Error('useChannel - ERROR: ' + e.message);
    }
  };

  return { subscribe, unsubscribe, send };
}
